<template>
  <div class="nx-relation-group-card" :class="{
    'nx-relation-group-card--first': first,
    'nx-relation-group-card--last': last
    }">
    <nx-relation-card-collapsed
      v-if="isCollapsed"
      :error="!valid"
      :title="collapsedTitle"
      :image="collapsedImage"
      :icon="collapsedIcon"
      @click="collapse(false)"
    />
    <nx-relation-card-tools
      v-if="loadCardFormComponent"
      v-show="!isCollapsed"
      :collapsable="collapsable"
      :position="position"
      :count="count"
      :hide-tools="hideTools"
      :disabled="disabled"
      :dark-theme="darkTheme"
      :disable-sort="disableSort"
      :disable-delete="disableDelete"
      @collapse="collapse(true)"
      v-on="$listeners"
    >
      <component
        :is="component"
        :relation-type="relationType"
        :relation-name="relationName"
        :wizard-types="wizardTypes"
        :uuid="uuid"
        :scope="`${relationName}/${uuid}`"
        :value="value"
        :disabled="disabled"
        :disable-crop="disableCrop"
        :excluded-entity-ids="excludedEntityIds"
        v-on="$listeners"
        @form="handleFormEvent"
        v-bind="cardParams"
      />
    </nx-relation-card-tools>
  </div>
</template>

<script>
import NxRelationCardTools from '@/modules/relation/card/CardTools';
import NxRelationCardCollapsed from '@/modules/relation/card/CardCollapsed';
import isObject from 'lodash/isObject';
import { fetchEntityListImage } from '@/modules/entity/utils';

export default {
  name: 'nx-relation-card',
  components: { NxRelationCardCollapsed, NxRelationCardTools },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    component: {
      type: [String, Object],
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    collapsable: {
      type: Boolean,
    },
    collapsableDefaultValue: {
      type: Boolean,
    },
    cardParams: {
      type: Object,
      default: () => ({}),
    },
    relationType: {
      type: String,
      required: true,
    },
    relationName: {
      type: String,
      required: true,
    },
    wizardTypes: {
      type: Array,
      default: () => ([]),
    },
    excludedEntityIds: {
      type: Array,
      default: () => ([]),
    },
    position: {
      type: Number,
      required: true,
    },
    count: {
      type: Number,
      required: true,
    },
    hideTools: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    disableCrop: {
      type: Boolean,
    },
    disableSort: {
      type: Boolean,
    },
    disableDelete: {
      type: Boolean,
    },
    collapsedTitleField: {
      type: String,
    },
    collapsedImageField: {
      type: String,
    },
    collapsedIcon: {
      type: String,
    },
    darkTheme: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valid: true,
      collapsed: true,
      loadCardFormComponent: false,
      contentImage: null,
    };
  },
  methods: {
    collapse(isCollapsed) {
      if (!isCollapsed) {
        this.loadCardFormComponent = true;
      }
      this.$nextTick(() => {
        this.collapsed = isCollapsed;
      });
    },
    handleFormEvent(form) {
      const { valid } = form;
      this.valid = valid;
    },
    async getContentImage() {
      if (this.collapsedImage || !this.collapsable) {
        return;
      }
      if (this.contentId) {
        const image = await fetchEntityListImage(this.contentId, 'content');
        if (image && image.src) {
          this.contentImage = image.src;
        } else {
          this.contentImage = null;
        }
      }
    },
  },
  computed: {
    contentId() {
      if (!this.isEmpty && this.value.destContent) {
        return this.value.destContent.id;
      }
      return null;
    },
    first() {
      return this.position === 0;
    },
    last() {
      return this.position === (this.count - 1);
    },
    isEmpty() {
      const keys = Object.keys(this.value);
      return keys.length === 1 && keys[0] === 'uuid';
    },
    isCollapsed() {
      return this.collapsable && this.collapsed;
    },
    collapsedImage() {
      const candidates = [
        'clipping',
        'image',
      ];
      const key = this.collapsedImageField || candidates.find(candidate => !!this.value[candidate]);
      const image = this.value[key];
      if (!image) {
        return this.contentImage;
      }
      if (isObject(image) && image.src) {
        return image.src;
      }
      return image;
    },
    collapsedTitle() {
      if (!this.valid) {
        return this.$t('message.relation.invalidElement');
      }
      if (this.collapsedTitleField) {
        return this.value[this.collapsedTitleField] || null;
      }
      const candidates = [
        'title',
        'text',
        'summary',
        'articleTitle',
        'articleSummary',
        'name',
      ];
      const key = candidates.find(candidate => !!this.value[candidate]);
      return this.value[key] || null;
    },
  },
  created() {
    if (this.collapsedTitle || this.collapsedImage) {
      this.collapsed = this.collapsableDefaultValue;
    } else {
      this.collapsed = false;
    }
    if (this.isEmpty) {
      this.collapsed = false;
      this.loadCardFormComponent = true;
    }
    if (!this.isCollapsed) {
      this.loadCardFormComponent = true;
    }
  },
  watch: {
    contentId: {
      handler(newValue) {
        this.contentImage = null;
        this.getContentImage();
      },
      immediate: true,
    },
  },
};
</script>

<style lang="stylus" scoped>
.nx-relation-group-card {
  will-change auto;
  margin-top: 5px;
  &--first {
    margin-top: 10px;
  }
  &--last {
    margin-bottom: 10px;
  }
  .nx-relation-card-tools {
    will-change auto;
  }
}
</style>
